import React, { useState, useEffect, useCallback } from 'react';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { clientDashboardService } from '../../services/clientDashboard';

interface Props {
  refreshKey: number;
}

const ClientDashboardOrders: React.FC<Props> = ({ refreshKey }) => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [error, setError] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await clientDashboardService.getOrders({ page, limit: 20, busca: buscaAtiva || undefined });
      setOrders(res.data || []);
      setPagination(res.pagination);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Erro ao carregar pedidos. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, buscaAtiva]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, refreshKey]);

  const toggleExpand = async (numeroPedido: string) => {
    if (expanded === numeroPedido) {
      setExpanded(null);
      setOrderDetail(null);
      return;
    }

    setExpanded(numeroPedido);
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await clientDashboardService.getOrderDetail(numeroPedido);
      setOrderDetail(res.data);
    } catch (err: any) {
      setDetailError('Erro ao carregar detalhes do pedido.');
      setOrderDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setBuscaAtiva(busca);
  };

  return (
    <>
      <h2 className="client-dashboard-section-title">Listagem de Pedidos</h2>

      <form onSubmit={handleSearch} className="client-dashboard-filters" style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <input
            type="text"
            className="client-dashboard-config-input"
            placeholder="Buscar por pedido ou NFe..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{ width: '100%', paddingLeft: 40 }}
          />
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
        </div>
        <button type="submit" className="client-dashboard-btn client-dashboard-btn-primary">
          Buscar
        </button>
      </form>

      {error && (
        <div className="client-dashboard-error-message">{error}</div>
      )}

      {loading ? (
        <div className="client-dashboard-loading">
          <div className="client-dashboard-spinner" />
          Carregando pedidos...
        </div>
      ) : (
        <div className="client-dashboard-table-container">
          <table className="client-dashboard-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Pedido</th>
                <th>Paletes</th>
                <th>Etiquetas</th>
                <th>Divergência</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: '#64748b' }}>
                    Nenhum pedido encontrado
                  </td>
                </tr>
              ) : (
                orders.map((order: any, i: number) => (
                  <React.Fragment key={i}>
                    <tr
                      onClick={() => toggleExpand(order.numero_pedido)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {expanded === order.numero_pedido
                          ? <ChevronDown size={16} />
                          : <ChevronRight size={16} />}
                      </td>
                      <td style={{ fontWeight: 600 }}>{order.numero_pedido || '-'}</td>
                      <td>{order.total_paletes}</td>
                      <td>{order.total_etiquetas}</td>
                      <td>
                        <span className={`client-dashboard-badge ${order.tem_divergencia ? 'divergente' : 'concluido'}`}>
                          {order.tem_divergencia ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td>
                        {order.data
                          ? new Date(order.data).toLocaleDateString('pt-BR')
                          : '-'}
                      </td>
                    </tr>
                    {expanded === order.numero_pedido && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div style={{ padding: '16px 24px', background: 'rgba(15,23,42,0.4)' }}>
                            {detailLoading ? (
                              <div style={{ color: '#94a3b8', fontSize: 13 }}>Carregando detalhes...</div>
                            ) : orderDetail ? (
                              orderDetail.paletes?.map((palete: any, pi: number) => (
                                <div key={pi} style={{ marginBottom: 16 }}>
                                  <div style={{ fontWeight: 600, color: '#3b82f6', fontSize: 14, marginBottom: 8 }}>
                                    {palete.numero_palete ? `Palete ${String(palete.numero_palete).padStart(3, '0')}` : 'Sem Palete'}
                                  </div>
                                  <div style={{ paddingLeft: 16 }}>
                                    {palete.etiquetas?.map((etq: any, ei: number) => (
                                      <div key={ei} style={{
                                        display: 'flex', gap: 16, padding: '6px 0',
                                        fontSize: 13, color: '#e2e8f0',
                                        borderBottom: '1px solid rgba(148,163,184,0.05)'
                                      }}>
                                        <span style={{ color: '#94a3b8' }}>{etq.codigo_barras || '-'}</span>
                                        <span>Vol: {etq.volume_atual}/{etq.total_volumes_pedido}</span>
                                        <span className={`client-dashboard-badge ${
                                          etq.leitura_resultado === 'ok' ? 'concluido' :
                                          etq.leitura_resultado === 'duplicado' ? 'divergente' :
                                          etq.leitura_resultado === 'nao_encontrado' || etq.leitura_resultado === 'avaria' || etq.leitura_resultado === 'erro' ? 'divergente' :
                                          'pendente'
                                        }`} style={{ fontSize: 11 }}>
                                          {etq.leitura_resultado === 'ok' ? 'Conferido' :
                                           etq.leitura_resultado === 'duplicado' ? 'Duplicado' :
                                           etq.leitura_resultado === 'nao_encontrado' ? 'Não Encontrado' :
                                           etq.leitura_resultado === 'avaria' ? 'Avaria' :
                                           etq.leitura_resultado === 'erro' ? 'Erro' :
                                           etq.leitura_resultado === 'removido' ? 'Removido' :
                                           etq.status || 'Pendente'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))
                            ) : detailError ? (
                              <div className="client-dashboard-error-message" style={{ fontSize: 13 }}>{detailError}</div>
                            ) : (
                              <div style={{ color: '#64748b', fontSize: 13 }}>Sem detalhes</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>

          {pagination && pagination.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                className="client-dashboard-btn client-dashboard-btn-primary"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                style={{ opacity: page <= 1 ? 0.5 : 1 }}
              >
                Anterior
              </button>
              <span style={{ color: '#94a3b8', fontSize: 13, alignSelf: 'center' }}>
                Página {page} de {pagination.totalPages}
              </span>
              <button
                className="client-dashboard-btn client-dashboard-btn-primary"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage(p => p + 1)}
                style={{ opacity: page >= pagination.totalPages ? 0.5 : 1 }}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ClientDashboardOrders;
